import { RouterParams } from 'App';
import { RootState } from 'app/store';
import { appInsights } from 'global/Application/AppInsight';
import { AppLinkTo, compareUnderWritingReviewTo, EViewType } from 'global/Application';
import { ESaveType, ISaveInstruction, SaveAction, SaveAsync } from "global/Application/dataActions";
import { trimGUIDLower } from 'global/Utility/TSFunctions';
import { ESnapshotStatus, setViewType, updateCreatingStatus } from 'global/Reducer/statusSlice';
import { bcbs_groupconfigurationsnapshot } from 'model/AttributeOptionSets.type';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { updateIteration } from 'global/Reducer/dataSlice';
import EntryDialog, { EEntryDialogActions, EEntryDialogVariant } from '../EntryDialog';
import GroupConfigurationProperties from '../GroupConfigurationProperties';
import { AppDataSource } from 'global/DataSources/AppDataSource';

interface ISnapshotDialogsProps {
    isChecklist?: boolean;
    dataSource: AppDataSource;
    isMergeApp?: boolean;
    isCompareApp?: boolean;
}

/**
 * Component to handle all dialog rendering (entry and properties)
 * Encapsulates complex dialog logic and handlers
 */
export const SnapshotDialogs = React.memo((props: ISnapshotDialogsProps) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { id: accountId, snapshotId } = useParams<RouterParams>();
    const state = useSelector((state: RootState) => state);

    const handleEntryDialogConfirm = useCallback(async (action: EEntryDialogActions) => {
        appInsights?.trackEvent({
            name: 'SnapshotDialogs.EntryDialog.onConfirm',
            properties: { action }
        });

        switch (action) {
            case EEntryDialogActions.View:
                dispatch(setViewType(EViewType.ViewOnly));
                break;

            case EEntryDialogActions.Modify:
                dispatch(setViewType(EViewType.Modify));
                dispatch(updateIteration());
                break;

            case EEntryDialogActions.ReopenAndModify:
                await SaveAsync(dispatch, props.dataSource, state, {
                    onSaveSetStatusCode: bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value,
                    onSaveStatusScreen: true
                });
                dispatch(setViewType(EViewType.Modify));
                dispatch(updateIteration());
                break;

            case EEntryDialogActions.Create:
                dispatch(setViewType(EViewType.Modify));
                dispatch(updateCreatingStatus(true));
                break;

            case EEntryDialogActions.UnderwritingReviewNew:
                dispatch(setViewType(EViewType.ViewOnly));
                {
                    const foundSnapshot = await compareUnderWritingReviewTo(state, state.data.snapshot)(dispatch);
                    history.push(AppLinkTo(state, {
                        type: 'underwriting',
                        id: foundSnapshot ? trimGUIDLower(foundSnapshot.bcbs_groupconfigurationsnapshotid!) : null
                    }));
                }
                break;

            case EEntryDialogActions.UnderwritingReviewLegacy:
                if (!state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid) return;

                dispatch(setViewType(EViewType.ViewOnly));
                {
                    const foundSnapshot = await compareUnderWritingReviewTo(state, state.data.snapshot)(dispatch);
                    if (foundSnapshot) {
                        const link = `/underwritingReview/${accountId}/${trimGUIDLower(foundSnapshot.bcbs_groupconfigurationsnapshotid!)}/${trimGUIDLower(state.data.snapshot.bcbs_groupconfigurationsnapshotid)}`;
                        history.push(link);
                    } else {
                        const link = `/underwritingReview/${accountId}/${snapshotId}/`;
                        history.push(link);
                    }
                }
                break;

            default:
                throw new Error("Unexpected action");
        }
    }, [dispatch, history, accountId, snapshotId, state, props.dataSource]);

    // #735944 - Allow the user to back out of the Group Configuration Properties setup panel
    // (e.g. Post Cancel Update creation) without creating/saving anything.
    const handlePropertiesCancel = useCallback(() => {
        dispatch(updateCreatingStatus(false));
        dispatch(setViewType(null));
    }, [dispatch]);

    const handlePropertiesConfirm = useCallback((gcPropertiesArgs: any) => {
        if (!gcPropertiesArgs) return;

        appInsights?.trackEvent({
            name: 'SnapshotDialogs.groupConfigurationProperties.onConfirm',
            properties: { value: gcPropertiesArgs.value }
        });

        const initializing = state.status.snapshotStatus === ESnapshotStatus.untracked;

        if (initializing) {
            const instructions: ISaveInstruction = {
                saveType: ESaveType.ECreate,
                onSaveSetStatusCode: bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value,
                onSaveStatusScreen: true,
                onPreSaveUpdateJson: gcPropertiesArgs.value,
            };

            dispatch(SaveAction(props.dataSource, history, instructions));
        } else {
            const instructions: ISaveInstruction = {
                onSaveClone: true,
                onCloneSetOriginatingSnapshotId: state.data.snapshot.bcbs_groupconfigurationsnapshotid,
                onSaveSetStatusCode: bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value,
                onSaveStatusScreen: true,
                onPostSaveNavigateExternalToSaveResult: true,
                onPreSaveUpdateJson: {
                    ...gcPropertiesArgs.value,
                    instructions: {
                        ...gcPropertiesArgs.value.instructions,
                        ignoreVisibilityRun: true,
                    }
                },
            };

            dispatch(SaveAction(props.dataSource, history, instructions));
        }
    }, [dispatch, history, state.status.snapshotStatus, state.data.snapshot.bcbs_groupconfigurationsnapshotid, props.dataSource]);


    let showEntryDialog = false;

    if (state.status.viewType === null)
        showEntryDialog = true;

    // Show entry dialog if no view type selected
    if (showEntryDialog) {
        return (
            <EntryDialog
                variant={EEntryDialogVariant.Default}
                isChecklist={props.isChecklist}
                onConfirm={handleEntryDialogConfirm}
            />
        );
    }

    let showPropertiesDialog = false;
    if (!props.isMergeApp && !props.isCompareApp)
        if (state.data.json.fields.length === 0 || !state.data.json.effectiveDate || !state.data.json.changeType || state.status.creating)
            showPropertiesDialog = true;

    // Show properties dialog if needed for setup
    if (showPropertiesDialog) {
        return (
            <GroupConfigurationProperties
                isOpen={true}
                variant='setup'
                defaultValue={state.data.json}
                onConfirm={handlePropertiesConfirm}
                onCancel={handlePropertiesCancel}
            />
        );
    }

    return null;
});

SnapshotDialogs.displayName = 'SnapshotDialogs';

export default SnapshotDialogs;
